// helpers/index.js

import {
    default_forms_domain,
    white_list_incoming_person_fields
} from './static_vars';
// Circular import is intentional and safe: both sides export only hoisted function
// declarations (`detect_parse_url_param_types`, `is_likely_matching_boolean_falsy_string`,
// `parse_boolean_falsy_types`), and none of them are called at module-evaluation time.
// This lets the simple `key:value` metadata parser reuse the same boolean / int / float /
// string sniffing the URL-param metadata path already uses, so values land in
// `page_metadata` / `form_metadata` with consistent types regardless of which lane a
// publisher chose.
import { detect_parse_url_param_types } from './parse_url_params';

export function get_boolean_query_param(param) {
    // If no param return null
    if (!param) { return null; }
    const value = new URLSearchParams(window.location.search).get(param);
    // if no value return null  
    if (!value) { return null; }
    return parse_boolean_falsy_types(value);
}

/**
 * Validates if a string is a valid URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
export function is_valid_url(url) {
    try {
        new URL(url);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * True when value is a plain (non-null, non-array) object. Canonical home for the
 * `value && typeof value === 'object' && !Array.isArray(value)` check that was being
 * re-inlined all over the codebase. Mirrors the local copies in web_push/analytics
 * (those can later import from here).
 *
 * @param {*} value
 * @returns {boolean}
 */
export function is_plain_object(value) {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * True when value is a plain object with at least one own key. Frontend mirror of
 * shared_functions `helpers.js#is_non_empty_object` (which webpack can't import from
 * `data-hub-shared-functions/`, only `static_vars` is synced).
 *
 * @param {*} value
 * @returns {boolean}
 */
export function is_non_empty_object(value) {
    return is_plain_object(value) && Object.keys(value).length > 0;
}


// TODO work on this. need to scope the loading check somehow? trying to remove window.references
// basically dont want to allow user to nav out if form is still POSTing
// const default_unload_function = (event, error_function = null, is_submission_in_progress = null) => {
//     event.preventDefault();
//     //event.returnValue = '';
//     console.log('debug: could call shared amplitude conversion here is_submission_in_progress', is_submission_in_progress, 'error_function', error_function);

//     if (window.is_data_hub_form_submission_in_progress && window.is_data_hub_form_submission_in_progress === true) {
//         const errorFunc = error_function || todo removed window.data_hub_error_function || ((msg) => console.error(msg));
//         //event.returnValue = 'A form submission is in progress. Are you sure you want to leave?';
//         errorFunc('A form submission is in progress. Are you sure you want to leave?');
//     }
// };

// Quirks with url param. Allow parsing a literal quote string
export function parse_int_or_return_null(value) {

    //if value is a string and starts and ends with quote
    if (typeof value === 'string' && value.startsWith('"') && value.endsWith('"')) {
        // Check that there are only two quotes in the entire string
        if (value.match(/"/g).length === 2) {
            value = value.slice(1, -1);
        }
    }

    // do the same for single quote ''
    if (typeof value === 'string' && value.startsWith("'") && value.endsWith("'")) {
        // Check that there are only two quotes in the entire string
        if (value.match(/'/g).length === 2) {
            value = value.slice(1, -1);
        }
    }

    const parsed = parseInt(parseFloat(value));
    return isNaN(parsed) ? null : parsed;
}

export function parse_float_or_return_null(value) {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;

}

export function is_likely_matching_boolean_falsy_string(the_value) {
    const truthy_values = ['true', 't', '1', 'yes', 'on'];
    const falsy_values = ['false', 'f', '0', 'no', 'off'];

    if (typeof the_value === 'string') {
        if (truthy_values.includes(the_value.toLowerCase()) || falsy_values.includes(the_value.toLowerCase())) {
            return true;
        }
    }
    return false;

}

export function parse_boolean_falsy_types(the_value) {
    const truthy_values = ['true', 't', '1', 'yes', 'on'];
    const falsy_values = ['false', 'f', '0', 'no', 'off'];

    if (typeof the_value === 'string') {
        if (truthy_values.includes(the_value.toLowerCase())) {
            return true;
        } else if (falsy_values.includes(the_value.toLowerCase())) {
            return false;
        }
    } else if (typeof the_value === 'boolean') {
        return the_value;
    } else if (typeof the_value === 'number') {
        return the_value !== 0;
    } else {
        console.error('parse_boolean_falsy_types: value is not a string, boolean, or number', the_value);
        return null;
    }
}

/**
 * Fetches a URL with a timeout.
 *
 * On timeout, ALWAYS throws a TimeoutError (named, with .timeout, .elapsed_ms,
 * .resource fields). Previously this function silently swallowed AbortError and
 * returned undefined, which crashed downstream callers with cryptic messages
 * like "Cannot read properties of undefined (reading 'ok')". Callers wrap in
 * try/catch — letting the error surface explicitly is strictly better.
 *
 * Two timeout modes:
 *   - abort_on_timeout: true (default) — cancel the fetch when the timer fires.
 *     Best for idempotent reads where a stale connection is wasteful.
 *   - abort_on_timeout: false — let the fetch keep running after the timer
 *     fires. The promise rejects with TimeoutError immediately, but the
 *     underlying request is still in flight, so a late response/error can be
 *     observed in DevTools or captured via `on_late_response`. Best for
 *     one-shot writes (form POSTs) where seeing what the server eventually
 *     said is more useful than canceling.
 *
 * @param {string} resource
 * @param {Object} [options] - Fetch options plus:
 *   - timeout {number} ms before timing out (default 10000)
 *   - abort_on_timeout {boolean} cancel underlying fetch on timeout (default true)
 *   - on_late_response {function} called when the fetch settles AFTER the
 *     timeout has fired. Receives ({ response, error, elapsed_ms }). Only
 *     invoked when abort_on_timeout is false.
 */
export async function fetch_with_timeout(resource, options = {}) {
    const {
        timeout = 20000,
        abort_on_timeout = true,
        on_late_response,
        ...fetch_options
    } = options;

    const start = Date.now();

    const make_timeout_error = (elapsed_ms) => {
        const error = new Error(
            `Request to ${resource} timed out after ${elapsed_ms}ms (limit: ${timeout}ms)`
        );
        error.name = 'TimeoutError';
        error.timeout = timeout;
        error.elapsed_ms = elapsed_ms;
        error.resource = resource;
        return error;
    };

    if (abort_on_timeout) {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(resource, {
                ...fetch_options,
                signal: controller.signal
            });
            clearTimeout(id);
            return response;
        } catch (error) {
            clearTimeout(id);
            if (error.name === 'AbortError') {
                throw make_timeout_error(Date.now() - start);
            }
            throw error;
        }
    }

    // Soft timeout: race the fetch against a timer, but DO NOT abort the fetch.
    // If the timer wins, callers see TimeoutError immediately, but the underlying
    // request keeps running so a late response is visible in the network panel
    // and reported to on_late_response.
    let timed_out = false;

    const handle_late_event = (payload) => {
        const elapsed_ms = Date.now() - start;
        const merged = { ...payload, elapsed_ms };
        if (typeof on_late_response === 'function') {
            try { on_late_response(merged); } catch (e) { /* swallow consumer hook errors */ }
        } else {
            const status_or_err = payload.response
                ? `status ${payload.response.status}`
                : `error ${payload.error?.message || payload.error}`;
            console.warn(
                `fetch_with_timeout: late settle (${elapsed_ms}ms) for ${resource} — ${status_or_err}`
            );
        }
    };

    const fetch_promise = fetch(resource, fetch_options).then(
        (response) => {
            if (timed_out) handle_late_event({ response, error: null });
            return response;
        },
        (error) => {
            if (timed_out) handle_late_event({ response: null, error });
            throw error;
        }
    );

    const timeout_promise = new Promise((_, reject) => {
        setTimeout(() => {
            timed_out = true;
            reject(make_timeout_error(Date.now() - start));
        }, timeout);
    });

    return Promise.race([fetch_promise, timeout_promise]);
}

// New function, mainly used in checking existing actblue.js so can call setInterval a max n number of time / seconds
export const poll_with_retry = (checkFn, options = {}) => {
    const { interval = 100, timeout = 5000 } = options;
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.floor(timeout / interval);

    return new Promise((resolve, reject) => {
        const check = () => {
            attempts++;
            if (checkFn()) {
                resolve(true);
            } else if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
                reject(new Error(`Polling timed out after ${timeout}ms`));
            } else {
                setTimeout(check, interval);
            }
        };
        check();
    });
};

// 250427 added new skip empty arrays to allow over-write of message type subscriptions to unsub/send empties
export function remove_empty_values_from_object(data, depth = 0, max_depth = 10, empty_array_keys_to_skip = []) {
    if (!empty_array_keys_to_skip || typeof empty_array_keys_to_skip !== 'object') { empty_array_keys_to_skip = []; }
    if (depth > max_depth) {
        console.log(`Soft error: maximum recursion depth of ${max_depth} exceeded`);
        return data;
    }

    if (Array.isArray(data)) {
        return data
            .filter(item =>
                item !== undefined &&
                item !== null &&
                item !== '' &&
                !(Array.isArray(item) && item.length === 0) &&
                !(item.constructor === Object && Object.keys(item).length === 0)
            )
            .map(item => remove_empty_values_from_object(item, depth + 1, max_depth, empty_array_keys_to_skip));
    } else if (data !== null && typeof data === 'object') {
        return Object.entries(data).reduce((acc, [key, value]) => {
            if (
                value !== undefined &&
                value !== null &&
                value !== '' &&
                !(Array.isArray(value) && value.length === 0 && !empty_array_keys_to_skip.includes(key)) &&
                !(value.constructor === Object && Object.keys(value).length === 0)
            ) {
                acc[key] = remove_empty_values_from_object(value, depth + 1, max_depth, empty_array_keys_to_skip);
            }
            return acc;
        }, {});
    }
    return data;
}

// formerly return_valid_forms_domain
export function return_valid_or_default_forms_domain(forms_domain) {
    // Default domain is the shared pooled domain for caching user data
    if (!forms_domain) {
        forms_domain = default_forms_domain
    }
    // Remove trailing slash if present
    if (forms_domain.endsWith('/')) {
        forms_domain = forms_domain.slice(0, -1);
    }
    // Ensure domains start with 'https://'
    if (!forms_domain.startsWith('https://')) {
        console.error('ERROR: forms_domain must start with https://');
        return null;
    }
    return forms_domain;
}

export function simple_string_to_html_basic_sanitization(input) {
    try {
        // Define allowed tags and attributes
        const allowedTags = [
            'b', 'i', 'em', 'strong', 'a', 'p', 'div', 'span',
            'ul', 'ol', 'li', 'br', 'img', 'h1', 'h2', 'h3',
            'h4', 'h5', 'h6', 'blockquote', 'pre', 'code'
        ];

        const allowedAttributes = {
            'a': ['href', 'title', 'style', 'target', 'rel'],
            'img': ['src', 'alt', 'title', 'style', 'width', 'height'],
            '*': ['style', 'class']
        };

        // Create a temporary DOM element to parse the input
        const parser = new DOMParser();
        const doc = parser.parseFromString(input, 'text/html');

        // Function to recursively sanitize nodes
        function sanitizeNode(node) {
            const nodeName = node.nodeName.toLowerCase();

            if (node.nodeType === Node.TEXT_NODE) {
                return node.cloneNode();
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (allowedTags.includes(nodeName)) {
                    // Create a new element to avoid inheriting unwanted attributes
                    const newEl = document.createElement(nodeName);

                    // Copy allowed attributes
                    const allowedAttrs = allowedAttributes[nodeName] || allowedAttributes['*'] || [];
                    for (let attr of node.attributes) {
                        if (allowedAttrs.includes(attr.name.toLowerCase())) {
                            newEl.setAttribute(attr.name, attr.value);
                        }
                    }

                    // Recursively sanitize and append child nodes
                    for (let child of node.childNodes) {
                        const sanitizedChild = sanitizeNode(child);
                        if (sanitizedChild) {
                            newEl.appendChild(sanitizedChild);
                        }
                    }
                    return newEl;
                } else {
                    // If tag is not allowed, process its children but skip the tag itself
                    const fragment = document.createDocumentFragment();
                    for (let child of node.childNodes) {
                        const sanitizedChild = sanitizeNode(child);
                        if (sanitizedChild) {
                            fragment.appendChild(sanitizedChild);
                        }
                    }
                    return fragment;
                }
            } else {
                // For other node types (e.g., comments), skip
                return null;
            }
        }

        // Sanitize the body content
        const sanitizedFragment = document.createDocumentFragment();
        for (let child of doc.body.childNodes) {
            const sanitizedChild = sanitizeNode(child);
            if (sanitizedChild) {
                sanitizedFragment.appendChild(sanitizedChild);
            }
        }

        // Serialize the sanitized content back to a string
        const div = document.createElement('div');
        div.appendChild(sanitizedFragment);
        return div.innerHTML;
    } catch (e) {
        console.error('simpleSanitizeHTML error:', e);
        return '';
    }

}

/**
 * Replaces tokens in the form {{ key }} with their values from data,
 * but only if the key is allowed by the provided whiteList.
 *
 * If a token is not allowed, it is simply removed from the string.
 *
 * @param {string} str - The string containing placeholder tokens.
 * @param {object} data - The data object with replacement values.
 * @param {object|Array} whiteList - Either an array of allowed keys or an object
 *                                   whose own properties are the allowed keys.
 */
export const replaceWhiteListedPersonPlaceholders = (str, data, whiteList) => {
    try {
        return str.replace(/{{\s*(.*?)\s*}}/g, (match, key) => {
            key = key.trim();
            let isKeyAllowed = false;

            if (Array.isArray(whiteList)) {
                isKeyAllowed = whiteList.includes(key);
            } else if (whiteList && typeof whiteList === 'object') {
                isKeyAllowed = Object.prototype.hasOwnProperty.call(whiteList, key);
            }

            // If allowed, replace with data value (or empty string if undefined).
            // Otherwise, remove the token entirely.
            return isKeyAllowed ? (data[key] || '') : '';
        });
    } catch (e) {
        console.error(e);
        return str;
    }
};

/**
* A simple template processor that replaces tokens of the form
*   {{ allowed.token }}
* with values from the context object. Uses real Handlebars if available.
*
* Any token whose content is not purely alphanumeric (plus underscores and dots)
* is simply removed.
*
* @param {string} templateString - The template string containing {{ tokens }}.
* @param {object} context - An object containing keys/values to replace.
* @returns {string} - The processed string.
*/
export const processHandlebarsTemplate = (templateString, context = {}) => {
    // exit if not templateString or context is empty
    if (!templateString || typeof templateString !== 'string'
        || !context || typeof context !== 'object' || Object.keys(context).length === 0) {
        return templateString;
    }
    try {
        // Check if Handlebars is loaded and usable.
        if (
            typeof Handlebars !== 'undefined' &&
            typeof Handlebars.compile === 'function'
        ) {
            return Handlebars.compile(templateString)(context);
        }

        // Fallback simple template processor:
        // Replace any {{ key }} or {{ nested.key }} occurrences.
        return templateString.replace(/{{\s*([^}]+?)\s*}}/g, (match, tokenContent) => {
            // Trim the token content.
            const token = tokenContent.trim();

            // Allowed tokens: only letters, digits, underscores and dots.
            // For example: "user_data.first_name"
            if (/^[A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*$/.test(token)) {
                // Use the dot-separated keys to retrieve the value from the context.
                const value = token.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), context);
                return value !== undefined && value !== null ? value : '';
            }
            // For any token that doesn't match the allowed pattern (such as helpers, conditionals, etc.),
            // simply remove it.
            return '';
        });
    } catch (error) {
        console.error('Error processing Handlebars template:', error);
        return templateString;
    }
};

export const replace_white_listed_person_placeholders = (str, data) => {
    try {
        // look for handlebars {{}} and replace with data, but only if the key for data is in the object white_list_incoming_person_fields
        return str.replace(/{{(.*?)}}/g, (match, key) => {
            key = key.trim();

            let isKeyAllowed = false;

            if (Array.isArray(white_list_incoming_person_fields)) {
                isKeyAllowed = white_list_incoming_person_fields.includes(key);
            } else if (typeof white_list_incoming_person_fields === 'object' && white_list_incoming_person_fields !== null) {
                isKeyAllowed = white_list_incoming_person_fields.hasOwnProperty(key);
            }

            return isKeyAllowed ? data[key] || '' : match;

        });
    } catch (e) {
        console.error(e)
    }
    return str
}

export const get_most_parent_url_shadow_dom_safe = () => {
    try {
        // Check if the script is running in a Shadow DOM context
        if (document.currentScript?.ownerDocument?.defaultView?.top) {
            return document.currentScript.ownerDocument.defaultView.top.location.href.split('?')[0];
        }

        // Check if the script is running in an iframe
        if (window.self !== window.top) {
            try {
                return window.top.location.href.split('?')[0];
            } catch (e) {
                console.error("Cross-origin issue accessing iframe parent URL:", e);
                return document.referrer.split('?')[0]; // Fallback to referrer if available
            }
        }
    } catch (e) {
        console.error("Error determining parent URL:", e);
    }

    // Default to the current window's URL if all else fails
    return window.location.href.split('?')[0];
};

/**
 * Filters an array to include only numbers.
 * @param {Array} the_array - The array to filter.
 * @param {boolean} [ints_only=true] - If true, only integers are included. If false, both integers and floats are allowed.
 * @returns {Array<number>} - An array containing only numbers based on the `ints_only` flag.
 */
export const return_only_numbers_in_array = (the_array, ints_only = true) => {
    if (!Array.isArray(the_array)) {
        console.error('return_only_numbers_in_array: Expected an array, received:', the_array, typeof the_array);
        return [];
    }

    return the_array.reduce((accumulator, val) => {
        let num;

        if (ints_only) {
            num = parseInt(val, 10);
        } else {
            num = parseFloat(val);
        }

        if (!isNaN(num)) {
            accumulator.push(num);
        } else {
            console.warn(`return_only_numbers_in_array: Value "${val}" is not a valid number.`);
        }

        return accumulator;
    }, []);
};

/**
 * Converts a string representation of an array or a comma-separated string into an array of numbers.
 * @param {string|Array} input - The string or array to convert.
 * @param {boolean} [ints_only=false] - If true, only integers are included. If false, both integers and floats are allowed.
 * @returns {Array<number>} - An array of numbers.
 */
export const string_array_of_ints_to_array = (input, ints_only = false) => {
    let parsedArray = [];

    if (typeof input === 'string') {
        input = input.trim();

        // Check if the string starts with '[' and ends with ']', indicating a JSON array
        if (input.startsWith('[') && input.endsWith(']')) {
            try {
                parsedArray = JSON.parse(input);
                if (!Array.isArray(parsedArray)) {
                    console.error('string_array_of_ints_to_array: Parsed JSON is not an array:', parsedArray);
                    return [];
                }
            } catch (e) {
                console.error('string_array_of_ints_to_array: Error parsing JSON string:', e, input);
                return [];
            }
        } else {
            // Assume it's a comma-separated string
            parsedArray = input.split(',').map(val => val.trim());
        }
    } else if (Array.isArray(input)) {
        parsedArray = input;
    } else {
        console.error('string_array_of_ints_to_array: Input is neither a string nor an array:', input, typeof input);
        return [];
    }

    // Filter and convert to numbers
    const numbers_only = return_only_numbers_in_array(parsedArray, ints_only);
    return numbers_only;
};

/**
 * Converts a string (e.g. "metadata_keyName", "METADATAKEYNAME", "key-name") 
 * into camelCase. 
 * @param {string} str - The string to convert.
 * @returns {string} - The camelCase string.
 */
export const toCamelCase = (str) => {
    return str
        // Replace any separator (hyphen, underscore, or space) followed by a character with the uppercase character.
        .replace(/[-_\s]+(.)/g, (_, group1) => group1.toUpperCase())
        // Lowercase the first character.
        .replace(/^(.)/, (match, group1) => group1.toLowerCase());
};

/**
 * Normalizes a metadata object's **keys** only.
 * If a key starts with "metadata" (any case), that prefix is removed.
 * Then all keys are converted into camelCase.
 * This allows metadata keys to be passed in as either:
 *   metadata_keyname, metadataKeyName, metadatakeyname, or simply keyname.
 *
 * **Values pass through untouched** — whatever type the upstream parser produced
 * (base64-JSON preserves publisher types; simple `key:value` and URL params are sniffed
 * via `detect_parse_url_param_types`). If you need a value-level transform, do it before
 * calling this, not here, so the key-normalization stays predictable.
 *
 * @param {object} metadataObj - The raw metadata object.
 * @param {function} [error_function] - Optional error callback.
 * @returns {object} - An object with normalized metadata keys and original values.
 */
export const normalize_metadata_from_object = (metadataObj, error_function = window?.data_hub?.error_function) => {
    if (!metadataObj || typeof metadataObj !== 'object') return {};
    const normalized = {};
    for (let [key, value] of Object.entries(metadataObj)) {
        try {
            if (typeof key !== 'string') {
                error_function?.(`Metadata key is not a string: ${key}`);
                continue;
            }
            let normalizedKey;
            // Check if the key begins with "metadata" (ignoring case)
            if (key.toLowerCase().startsWith('metadata')) {
                // Remove the first 8 characters ("metadata") and any extra underscore/dash that might follow.
                let stripped = key.slice(8);
                // If the stripped key begins with a non-alphanumeric character, remove it.
                stripped = stripped.replace(/^[-_\s]+/, '');
                normalizedKey = toCamelCase(stripped);
            } else {
                normalizedKey = toCamelCase(key);
            }
            normalized[normalizedKey] = value;
        } catch (error) {
            error_function?.(`Error normalizing metadata key-value pair: ${key} = ${value}, ${error}`);
            continue;
        }
    }
    return normalized;
};


/**
 * Parse a dataset attribute that may be either a base64-encoded JSON blob or a loose
 * `key:value,key2:value2` string. Used for `data-hub-form-metadata`, `data-hub-page-metadata`,
 * `data-hub-refcodes`, etc.
 *
 * **Type contract — important when the parsed object becomes Iterable `dataFields`:**
 *   - **Base64-JSON path** (preferred): types are preserved verbatim from the publisher's JSON.
 *     `{vip:true}` stays a boolean; `{count:5}` stays a number; `{count:"5"}` stays a string.
 *     This is the explicit-types lane — publishers who need a specific type must use this form.
 *   - **Simple `key:value` fallback**: values are type-sniffed via
 *     {@link parse_simple_key_value_string} → {@link detect_parse_url_param_types}, matching
 *     the URL-param coercion rules (bool / int / float / string).
 *
 * **Drift caveat:** even with the two non-JSON paths harmonized, a publisher who base64-JSONs
 * `{vip:true}` on one page and `{vip:"true"}` on another will still send Iterable mismatched
 * types. Iterable locks each field's type on first write and rejects the second with
 * `{code, msg, params:{validationErrors:{<field>:{expectedType}}}}`. The reactive fallback
 * lives in `iterable-api-process/iterable/iterable_type_error_transform.js` which coerces and
 * retries (capped at 2 attempts) before nulling the field. Keep this in mind when adding new
 * publisher-facing metadata attributes — don't introduce a fourth lane with different rules.
 *
 * @param {string} inputString
 * @param {Function} [error_function]
 * @returns {Object}
 */
export const parse_b64_or_array_like_data_attribute = (inputString, error_function = window?.data_hub?.error_function) => {
    if (!inputString || typeof inputString !== 'string' || inputString.trim() === '') return {};
    inputString = inputString.trim();

    try {
        const decodedJson = atob(inputString);
        const parsedJson = JSON.parse(decodedJson);
        if (typeof parsedJson === 'object' && parsedJson !== null) {
            return parsedJson;
        }
    } catch (e) {
        // Failed base64 JSON decode; fall through.
    }

    const simpleParsed = parse_simple_key_value_string(inputString, error_function);
    if (simpleParsed && Object.keys(simpleParsed).length > 0) {
        return simpleParsed;
    }

    error_function?.(`Invalid data attribute format: ${inputString}`);
    return {};
};

/**
 * Parse a loose `key:value,key2:value2` (or `=`-delimited, optionally `[...]`-wrapped) string
 * into an object. Used as the fallback for the `data-hub-form-metadata` / `data-hub-page-metadata`
 * dataset attributes when the value isn't a base64-encoded JSON blob.
 *
 * Values are run through {@link detect_parse_url_param_types} so the type sniffing matches the
 * URL-param metadata path (`?metadata_foo=bar`). That means:
 *   - `vip:true`  → boolean `true`
 *   - `count:5`   → integer `5`
 *   - `ratio:0.5` → float `0.5`
 *   - anything else → trimmed string
 *
 * **Why this matters:** the parsed object flows into `page_metadata` / `form_metadata`, which
 * are merged into the `dataFields` of the Iterable `events/track` POST and into POST `user_data`.
 * Iterable locks each field's type on first write; if two pages send the same key with different
 * types, the second one is rejected. Harmonizing this parser with the URL-param parser removes
 * the most common source of that drift (publishers mixing `data-hub-form-metadata="vip:true"`
 * with `?metadata_vip=true`). If a publisher needs an explicit string for a value that looks
 * like a bool/number (e.g. a version string `"5.0.1"`), they should use the base64-JSON form
 * — see {@link parse_b64_or_array_like_data_attribute}.
 *
 * @param {string} inputString
 * @param {Function} [error_function]
 * @returns {Object<string, string|number|boolean|null>}
 */
export const parse_simple_key_value_string = (inputString, error_function = window?.data_hub?.error_function) => {
    if (!inputString || typeof inputString !== 'string') return {};
    inputString = inputString.trim();

    try {
        // Choose separator: if ':' exists use it; otherwise, '='.
        const separator = inputString.includes(':') ? ':' : '=';

        // Remove surrounding brackets if present.
        if (inputString.startsWith('[') && inputString.endsWith(']')) {
            inputString = inputString.slice(1, -1);
        }

        const keyValuePairs = inputString.split(',');
        const parsedObject = keyValuePairs.reduce((acc, pair) => {
            const [key, value] = pair.split(separator);
            if (key && value) {
                // detect_parse_url_param_types handles its own .trim() and returns
                // boolean / int / float / string per the same rules used for URL params.
                acc[key.trim()] = detect_parse_url_param_types(value);
            } else {
                error_function?.("Key-value not in correct format: " + pair);
            }
            return acc;
        }, {});
        return parsedObject;
    } catch (e) {
        console.error('Error parsing simple key-value string:', e);
        return {};
    }
}

/**
 * UTF-8-safe base64 JSON decoder for new attributes (e.g. `data-hub-form-fields`).
 * Bare `atob` returns a latin1 byte-string, so multibyte UTF-8 (curly quotes, emoji)
 * in the encoded JSON mojibakes through the legacy decoders. Publishers must encode
 * with `btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(x))))`.
 * No key:value fallback lane on purpose — garbage input must return null so callers
 * can fall back to their legacy rendering path instead of "succeeding" with `{}`.
 *
 * @param {string} base64String
 * @param {Function} [error_function]
 * @returns {Object|Array|null} parsed JSON, or null on any decode/parse failure.
 */
export const parse_base64_json_utf8 = (base64String, error_function = window?.data_hub?.error_function) => {
    try {
        const bytes = Uint8Array.from(atob(base64String.trim()), c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder('utf-8').decode(bytes));
    } catch (error) {
        error_function?.('Error parsing base64 UTF-8 JSON: ' + error);
        return null;
    }
};

// Mostly for parsing htmlElement data attributes
export const parse_base64_json = (base64String) => {
    try {
        const jsonString = atob(base64String);
        return JSON.parse(jsonString);
    } catch (error) {
        console.error('Error parsing base64 JSON:', error);
        return null;
    }
}

export const parse_custom_html = (inputString) => {
    let decodedString = inputString;
    // Try Base64 decode
    try {
        decodedString = atob(inputString);
        return decodedString;
    } catch (error) {
        // Not Base64, try URL decode
        try {
            decodedString = decodeURIComponent(inputString);
            return decodedString;
        } catch (error) {
            return decodedString;
        }
    }

}

// COLORS and tailwind css generators
// So can pass a primary 600 value and generate full 100 -> 900 gradient
export function hex_to_hsl(hex) {
    if (!hex) {
        console.error("hex_to_hsl missing hex");
        return null;
    }
    if (typeof hex !== 'string') {
        console.error("hex_to_hsl hex must be a string");
        return null;
    }

    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.slice(0, 2), 16);
    let g = parseInt(hex.slice(2, 4), 16);
    let b = parseInt(hex.slice(4, 6), 16);

    r /= 255;
    g /= 255;
    b /= 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return [h * 360, s * 100, l * 100];
}

export function hsl_to_hex(h, s, l) {
    h /= 360;
    s /= 100;
    l /= 100;

    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    const to_hex = x => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${to_hex(r)}${to_hex(g)}${to_hex(b)}`;
}

export function generate_gradient_stops(main_hex_color) {
    if (!main_hex_color) {
        console.error("generate_gradient_stops missing main_hex_color");
        return {};
    }

    // Must be a valid color hex color code (3 or 6 digits)
    if (typeof main_hex_color !== 'string' || !main_hex_color.match(/^#([0-9A-F]{3}|[0-9A-F]{6})$/i)) {
        console.error("generate_gradient_stops main_hex_color must be a valid hex color code");
        return {};
    }

    // Normalize 3-digit hex codes to 6 digits
    if (main_hex_color.length === 4) {
        main_hex_color = '#' + main_hex_color.slice(1).split('').map(char => char + char).join('');
    }

    const hsl = hex_to_hsl(main_hex_color);
    const stops = {};

    for (let i = 1; i <= 9; i++) {
        const lightness = 100 - (i * 10); // Reverse the lightness
        stops[(i * 100).toString()] = hsl_to_hex(hsl[0], hsl[1], lightness);
    }

    // Number 600 should be the main_hex_color
    stops['600'] = main_hex_color;

    return stops;
}


export function find_closest_shade(colors, target) {
    if (!colors || !target) {
        console.error("find_closest_shade missing vars");
        return null;
    }
    const keys = Object.keys(colors).map(Number).sort((a, b) => a - b);
    return keys.reduce((prev, curr) => Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev).toString();
}

export function to_kebab_case(str) {
    if (!str) {
        return null;
    }
    if (typeof str !== 'string') {
        return null;
    }
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase().replace(/_/g, '-');
}

function get_simple_hash(text_content) {
    let hash = 0, i, chr;
    if (text_content.length === 0) return hash.toString();
    for (i = 0; i < text_content.length; i++) {
        chr = text_content.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
}

export function inject_styles(theme, target) {
    if (!target) {
        console.error('Error inject_styles target is null:', target);
        return;
    }
    if (!theme) {
        console.error('Error inject_styles theme is null:', theme);
        return;
    }

    // Generate a short hash of the theme JSON as a key
    const theme_key_hash = get_simple_hash(JSON.stringify(theme)).slice(0, 8);

    // Single-stylesheet model: there is at most one <style data-theme-hash> in the target.
    // - Same hash → identical theme, no-op.
    // - Different hash → update the existing element in place (no DOM churn, no flash).
    // - None exists → insert a new one at the top of the target.
    const existingStyles = target.querySelectorAll('style[data-theme-hash]');
    if (existingStyles.length) {
        const primary = existingStyles[0];
        // Drop any extras from older multi-inject paths so we converge on one.
        for (let i = 1; i < existingStyles.length; i++) existingStyles[i].remove();
        if (primary.dataset.themeHash === theme_key_hash) return;
        primary.innerHTML = generate_theme_css(theme);
        primary.dataset.themeHash = theme_key_hash;
        return;
    }

    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = generate_theme_css(theme);
    style.dataset.themeHash = theme_key_hash;

    const has_first_child = target.firstChild;
    if (has_first_child) {
        //add to the top of target
        target.insertBefore(style, target.firstChild);
    } else {
        target.appendChild(style);
    }
}

function generate_theme_css(theme) {

    let css = '';

    if (!theme) {
        console.error('Error generate_theme_css theme is null:', theme);
        return css;
    }

    Object.keys(theme).forEach(color_key => {
        const color = theme[color_key];
        let colors = color;
        const color_key_kebab = to_kebab_case(color_key);

        if (typeof color === 'string') {
            colors = generate_gradient_stops(color);
        } else if (typeof color === 'object' && Object.keys(color).length < 9) {
            const closest_shade = find_closest_shade(color, 600);
            colors = { ...generate_gradient_stops(color[closest_shade]), ...color };
        } else if (typeof color !== 'object' || Object.keys(color).length != 9) {
            console.error('Error generate_theme_css color is not a string or object with 9 keys:', color);
            return;
        }

        const shade_entries = Object.keys(colors).filter((shade_key) => {
            if (!shade_key.toString().match(/^(100|200|300|400|500|600|700|800|900)$/)) {
                console.error('Error generate_theme_css shade_key is not a tailwind number:', shade_key);
                return false;
            }
            return true;
        });

        if (shade_entries.length > 0) {
            css += `
                :root {
`;
            shade_entries.forEach((shade_key) => {
                css += `                    --${color_key_kebab}-${shade_key}: ${colors[shade_key]};\n`;
            });
            css += `                }
`;
        }

        shade_entries.forEach((shade_key) => {
            const class_name = `${color_key_kebab}-${shade_key}`;
            css += `
                .text-${class_name} { color: ${colors[shade_key]}; }
                .bg-${class_name} { background-color: ${colors[shade_key]}; }
                .border-${class_name} { border-color: ${colors[shade_key]}; }
                .ring-${class_name} { ring-color: ${colors[shade_key]}; }
                .shadow-${class_name} { box-shadow: 0 1px 2px 0 ${colors[shade_key]}; }
                .placeholder-${class_name}::placeholder { color: ${colors[shade_key]}; }
                .divide-${class_name} > * + * { border-color: ${colors[shade_key]}; }
                .decoration-${class_name} { text-decoration-color: ${colors[shade_key]}; }
                .caret-${class_name} { caret-color: ${colors[shade_key]}; }
                .accent-${class_name} { accent-color: ${colors[shade_key]}; }
                .outline-${class_name} { outline-color: ${colors[shade_key]}; }
                .from-${class_name} { --tw-gradient-from: ${colors[shade_key]} var(--tw-gradient-from-position); }
                .ring-offset-${class_name} { --tw-ring-offset-color: ${colors[shade_key]}; }
                .shadow-${class_name} { --tw-shadow-color: ${colors[shade_key]}; }
                .fill-${class_name} { fill: ${colors[shade_key]}; }
                .stroke-${class_name} { stroke: ${colors[shade_key]}; }

                .hover\\:text-${class_name}:hover { color: ${colors[shade_key]}; }
                .hover\\:bg-${class_name}:hover { background-color: ${colors[shade_key]}; }
                .hover\\:border-${class_name}:hover { border-color: ${colors[shade_key]}; }
                .hover\\:ring-${class_name}:hover { ring-color: ${colors[shade_key]}; }
                .hover\\:shadow-${class_name}:hover { box-shadow: 0 1px 2px 0 ${colors[shade_key]}; }
                .hover\\:placeholder-${class_name}:hover::placeholder { color: ${colors[shade_key]}; }
                .hover\\:divide-${class_name}:hover > * + * { border-color: ${colors[shade_key]}; }

                .focus\\:text-${class_name}:focus { color: ${colors[shade_key]}; }
                .focus\\:bg-${class_name}:focus { background-color: ${colors[shade_key]}; }
                .focus\\:border-${class_name}:focus { border-color: ${colors[shade_key]}; }
                .focus\\:ring-${class_name}:focus { ring-color: ${colors[shade_key]}; }
                .focus\\:shadow-${class_name}:focus { box-shadow: 0 1px 2px 0 ${colors[shade_key]}; }
                .focus\\:placeholder-${class_name}:focus::placeholder { color: ${colors[shade_key]}; }
                .focus\\:divide-${class_name}:focus > * + * { border-color: ${colors[shade_key]}; }

                .active\\:text-${class_name}:active { color: ${colors[shade_key]}; }
                .active\\:bg-${class_name}:active { background-color: ${colors[shade_key]}; }
                .active\\:border-${class_name}:active { border-color: ${colors[shade_key]}; }
                .active\\:ring-${class_name}:active { ring-color: ${colors[shade_key]}; }
                .active\\:shadow-${class_name}:active { box-shadow: 0 1px 2px 0 ${colors[shade_key]}; }
                .active\\:placeholder-${class_name}:active::placeholder { color: ${colors[shade_key]}; }
                .active\\:divide-${class_name}:active > * + * { border-color: ${colors[shade_key]}; }
            `;
        });

        if (colors['600']) {
            css += `
                .text-${color_key_kebab} { color: ${colors['600']}; }
                .bg-${color_key_kebab} { background-color: ${colors['600']}; }
                .border-${color_key_kebab} { border-color: ${colors['600']}; }
                .ring-${color_key_kebab} { ring-color: ${colors['600']}; }
                .shadow-${color_key_kebab} { box-shadow: 0 1px 2px 0 ${colors['600']}; }
                .placeholder-${color_key_kebab}::placeholder { color: ${colors['600']}; }
                .divide-${color_key_kebab} > * + * { border-color: ${colors['600']}; }
                .decoration-${color_key_kebab} { text-decoration-color: ${colors['600']}; }
                .caret-${color_key_kebab} { caret-color: ${colors['600']}; }
                .accent-${color_key_kebab} { accent-color: ${colors['600']}; }
                .outline-${color_key_kebab} { outline-color: ${colors['600']}; }
                .from-${color_key_kebab} { --tw-gradient-from: ${colors['600']} var(--tw-gradient-from-position); }
                .ring-offset-${color_key_kebab} { --tw-ring-offset-color: ${colors['600']}; }
                .shadow-${color_key_kebab} { --tw-shadow-color: ${colors['600']}; }
                .fill-${color_key_kebab} { fill: ${colors['600']}; }
                .stroke-${color_key_kebab} { stroke: ${colors['600']}; }

                .hover\\:text-${color_key_kebab}:hover { color: ${colors['600']}; }
                .hover\\:bg-${color_key_kebab}:hover { background-color: ${colors['600']}; }
                .hover\\:border-${color_key_kebab}:hover { border-color: ${colors['600']}; }
                .hover\\:ring-${color_key_kebab}:hover { ring-color: ${colors['600']}; }
                .hover\\:shadow-${color_key_kebab}:hover { box-shadow: 0 1px 2px 0 ${colors['600']}; }
                .hover\\:placeholder-${color_key_kebab}:hover::placeholder { color: ${colors['600']}; }
                .hover\\:divide-${color_key_kebab}:hover > * + * { border-color: ${colors['600']}; }

                .focus\\:text-${color_key_kebab}:focus { color: ${colors['600']}; }
                .focus\\:bg-${color_key_kebab}:focus { background-color: ${colors['600']}; }
                .focus\\:border-${color_key_kebab}:focus { border-color: ${colors['600']}; }
                .focus\\:ring-${color_key_kebab}:focus { ring-color: ${colors['600']}; }
                .focus\\:shadow-${color_key_kebab}:focus { box-shadow: 0 1px 2px 0 ${colors['600']}; }
                .focus\\:placeholder-${color_key_kebab}:focus::placeholder { color: ${colors['600']}; }
                .focus\\:divide-${color_key_kebab}:focus > * + * { border-color: ${colors['600']}; }

                .active\\:text-${color_key_kebab}:active { color: ${colors['600']}; }
                .active\\:bg-${color_key_kebab}:active { background-color: ${colors['600']}; }
                .active\\:border-${color_key_kebab}:active { border-color: ${colors['600']}; }
                .active\\:ring-${color_key_kebab}:active { ring-color: ${colors['600']}; }
                .active\\:shadow-${color_key_kebab}:active { box-shadow: 0 1px 2px 0 ${colors['600']}; }
                .active\\:placeholder-${color_key_kebab}:active::placeholder { color: ${colors['600']}; }
                .active\\:divide-${color_key_kebab}:active > * + * { border-color: ${colors['600']}; }

                /* Loader Styles */
                .loader {
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid ${colors['600']};
                    border-radius: 50%;
                    width: 50px;
                    height: 50px;
                    animation: spin 2s linear infinite;
                }

                /* Spinner for Submit Button */
                .spinner {
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid ${colors['600']};
                    border-radius: 50%;
                    width: 20px;
                    height: 20px;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `;
        } else {
            console.error('Error generate_theme_css no 600 color_key:', color_key, 'color_key_kebab', color_key_kebab, 'colors:', colors);
        }
    });

    return css;
}